import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import { useSidebarState } from '../hooks/useSidebarState';
import { useIsMobile, useStandalone } from '../hooks/useIsMobile';
import { 
  Search, 
  Upload, 
  DollarSign, 
  Globe,
  TrendingUp,
  AlertTriangle,
  Package,
  BarChart3,
  PieChart,
  ArrowRight,
  Clock,
  FileText,
  Zap,
  AlertCircle,
  Activity,
  Smartphone,
  X,
  Download,
  Settings,
  Brain,
  ThumbsUp,
  Sparkles
} from 'lucide-react';
import { apiGet } from '../lib/apiClient';

// PWA Install Banner Component
const PWAInstallBanner = ({ onDismiss }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const isMobile = useIsMobile();
  const isStandalone = useStandalone();

  useEffect(() => {
    // Check if banner was dismissed
    const dismissed = localStorage.getItem('pwa_banner_dismissed');
    if (dismissed) return;

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show banner for mobile users even without the prompt (for iOS)
    if (isMobile && !isStandalone) {
      setTimeout(() => setShowBanner(true), 2000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, [isMobile, isStandalone]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      // For iOS - show instructions
      alert('To install: Tap the Share button, then "Add to Home Screen"');
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_banner_dismissed', 'true');
    setShowBanner(false);
    onDismiss?.();
  };

  if (!showBanner || isStandalone) return null;

  return (
    <div className="bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] text-white px-4 py-3 rounded-xl mb-6 flex items-center justify-between shadow-lg">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
          <Smartphone className="w-5 h-5" />
        </div>
        <div>
          <p className="font-semibold text-sm">Install Decoded App</p>
          <p className="text-xs text-white/80">Quick access to tariff classification on the go</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          onClick={handleInstall}
          size="sm"
          className="bg-white text-[#0B3D91] hover:bg-white/90 text-xs px-3 h-8"
          data-testid="pwa-install-btn"
        >
          <Download className="w-3 h-3 mr-1" />
          Install
        </Button>
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-white/20 rounded"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const DashboardHome = ({ user, setUser }) => {
  const navigate = useNavigate();
  const sidebarCollapsed = useSidebarState();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchAiInsights();
  }, []);

  const fetchAiInsights = async () => {
    try {
      const data = await apiGet('/api/ai/insights');
      if (data) setAiInsights(data);
    } catch (e) {
      console.error('Failed to fetch AI insights', e);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch analyses for stats + financial cache stats in parallel
      const [analysesData, financialData] = await Promise.all([
        apiGet('/api/analyses').catch(() => null),
        apiGet('/api/financial/cache-stats').catch(() => null),
      ]);

      let analyses = [];
      let totalSkus = 0;
      let totalSavings = 0;
      let flaggedItems = 0;
      let highConfidence = 0;
      let totalAnalyses = 0;

      if (analysesData) {
        analyses = analysesData.analyses || analysesData || [];
        totalAnalyses = analyses.length;

        // Calculate stats from analyses summary data
        analyses.forEach(analysis => {
          totalSkus += analysis.total_skus || 1;
          totalSavings += analysis.total_estimated_savings || 0;

          // Count high confidence from distribution
          const confDist = analysis.confidence_distribution || {};
          highConfidence += confDist.high || 0;

          // Count flagged items (low confidence or compliance issues)
          flaggedItems += confDist.low || 0;
        });
      }

      // Get recent activity
      const recent = analyses.slice(0, 5).map(a => ({
        id: a.analysis_id,
        type: a.type || 'single',
        title: a.name || a.file_name || 'Product Analysis',
        date: a.created_at,
        skus: a.total_skus || 1,
        savings: a.total_estimated_savings || 0
      }));

      setRecentActivity(recent);
      setDashboardData({
        totalAnalyses,
        totalSkus,
        totalSavings,
        flaggedItems,
        highConfidenceRate: totalSkus > 0 ? Math.round((highConfidence / totalSkus) * 100) : 0,
        hasData: totalAnalyses > 0
      });
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      setDashboardData({ hasData: false, totalAnalyses: 0, totalSkus: 0, totalSavings: 0, flaggedItems: 0, highConfidenceRate: 0 });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const isMobile = useIsMobile();

  // Quick Action Cards
  const quickActions = [
    { 
      label: 'Analyze Product', 
      icon: Search, 
      href: isMobile ? '/mobile' : '/analyzer',
      color: 'from-[#2563EB] to-[#3B82F6]',
      description: isMobile ? 'Voice, camera or text' : 'Single product classification'
    },
    { 
      label: 'Upload Catalog', 
      icon: Upload, 
      href: '/analyzer?mode=bulk',
      color: 'from-[#06B6D4] to-[#22D3EE]',
      description: 'Bulk CSV/Excel analysis'
    },
    { 
      label: 'Analyze Transactions', 
      icon: DollarSign, 
      href: '/analyzer?tab=financial',
      color: 'from-[#059669] to-[#10B981]',
      description: 'Financial impact analysis'
    },
    { 
      label: 'Optimize Sourcing', 
      icon: Globe, 
      href: '/analyzer?tab=sourcing',
      color: 'from-[#8B5CF6] to-[#A78BFA]',
      description: 'Find better sourcing options'
    }
  ];

  const hasData = dashboardData?.hasData;

  // Mobile Dashboard View - matches MobileAnalyzer style
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] pb-20">
        {/* Mobile Header */}
        <div className="bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] text-white px-4 pt-12 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Zap className="w-7 h-7" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl font-bold">Decoded</h1>
              <p className="text-white/80 text-sm">
                {user?.name ? `Welcome, ${user.name.split(' ')[0]}` : 'Dashboard'}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions - Floating Cards */}
        <div className="px-4 -mt-4">
          <div className="bg-white rounded-2xl shadow-lg p-3">
            <div className="grid grid-cols-4 gap-2">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => navigate(action.href)}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl hover:bg-slate-50 transition-colors"
                  data-testid={`quick-action-mobile-${i}`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center`}>
                    <action.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-[10px] font-medium text-slate-600 text-center leading-tight">
                    {action.label.split(' ')[0]}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 py-4 space-y-4">
          {/* PWA Install Banner */}
          <PWAInstallBanner />

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : !hasData ? (
            /* First-time User - Welcome Card */
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h2 className="font-outfit text-xl font-bold text-slate-900 mb-2">
                Get Started
              </h2>
              <p className="text-slate-600 text-sm mb-4">
                Analyze your first product to discover tariff savings.
              </p>
              <Button 
                onClick={() => navigate('/mobile')}
                className="w-full bg-gradient-to-r from-[#0B3D91] to-[#1E5EFF] text-white py-5"
              >
                <Search className="w-5 h-5 mr-2" />
                Analyze Product
              </Button>
            </div>
          ) : (
            /* Dashboard with Data */
            <>
              {/* Savings Card */}
              <div className="bg-gradient-to-br from-[#059669] to-[#10B981] rounded-2xl p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-white/80 text-sm font-medium">Savings Identified</span>
                </div>
                <div className="text-3xl font-bold mb-1">
                  {formatCurrency(dashboardData.totalSavings || 0)}
                </div>
                <p className="text-white/70 text-xs">
                  Across {dashboardData.totalSkus} SKUs
                </p>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                  <div className="text-2xl font-bold text-[#2563EB]">{dashboardData.totalAnalyses}</div>
                  <p className="text-[10px] text-slate-500">Analyses</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                  <div className="text-2xl font-bold text-[#06B6D4]">{dashboardData.totalSkus}</div>
                  <p className="text-[10px] text-slate-500">SKUs</p>
                </div>
                <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                  <div className="text-2xl font-bold text-[#10B981]">{dashboardData.highConfidenceRate}%</div>
                  <p className="text-[10px] text-slate-500">Confidence</p>
                </div>
              </div>

              {/* Risk Alerts */}
              {dashboardData.flaggedItems > 0 && (
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    <span className="font-semibold text-slate-900 text-sm">Attention Needed</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                    <span className="text-sm text-slate-700">High Risk Items</span>
                    <span className="font-bold text-red-600">{dashboardData.flaggedItems}</span>
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {recentActivity.length > 0 && (
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-slate-400" />
                      <span className="font-semibold text-slate-900 text-sm">Recent</span>
                    </div>
                    <button 
                      onClick={() => navigate('/history')}
                      className="text-xs text-[#2563EB] font-medium"
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-2">
                    {recentActivity.slice(0, 3).map((item, i) => (
                      <div 
                        key={i}
                        onClick={() => navigate(`/analysis/${item.id}`)}
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-xl cursor-pointer"
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                          item.type === 'bulk' ? 'bg-[#06B6D4]/10' : 'bg-[#2563EB]/10'
                        }`}>
                          {item.type === 'bulk' ? (
                            <FileText className="w-4 h-4 text-[#06B6D4]" />
                          ) : (
                            <Search className="w-4 h-4 text-[#2563EB]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{item.title}</p>
                          <p className="text-[10px] text-slate-400">{formatDate(item.date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 safe-area-bottom">
          <div className="flex justify-around">
            <button className="flex flex-col items-center gap-1 text-[#0B3D91]">
              <Zap className="w-6 h-6" />
              <span className="text-xs font-medium">Home</span>
            </button>
            <button 
              onClick={() => navigate('/mobile')}
              className="flex flex-col items-center gap-1 text-slate-400"
            >
              <Search className="w-6 h-6" />
              <span className="text-xs">Analyze</span>
            </button>
            <button 
              onClick={() => navigate('/history')}
              className="flex flex-col items-center gap-1 text-slate-400"
            >
              <FileText className="w-6 h-6" />
              <span className="text-xs">History</span>
            </button>
            <button 
              onClick={() => navigate('/settings')}
              className="flex flex-col items-center gap-1 text-slate-400"
            >
              <Settings className="w-6 h-6" />
              <span className="text-xs">Settings</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Dashboard View
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <Sidebar user={user} setUser={setUser} activePage="dashboard" />
      
      <main className="flex-1 p-4 lg:p-8 lg:pt-24 transition-all duration-300">
        <div className="max-w-7xl">
          
          {/* PWA Install Banner for Mobile */}
          <PWAInstallBanner />
          
          {/* Quick Actions Bar */}
          <div className="mb-5 lg:mb-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => navigate(action.href)}
                  className="group relative overflow-hidden bg-white rounded-xl lg:rounded-2xl p-4 lg:p-5 border border-slate-100 hover:border-transparent hover:shadow-xl transition-all duration-300"
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${action.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                  <div className="relative z-10">
                    <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg lg:rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-2 lg:mb-3 group-hover:bg-white/20 transition-colors`}>
                      <action.icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                    </div>
                    <p className="font-semibold text-sm lg:text-base text-slate-900 group-hover:text-white transition-colors">{action.label}</p>
                    <p className="text-[10px] lg:text-xs text-slate-500 group-hover:text-white/80 transition-colors mt-0.5 lg:mt-1 line-clamp-2">{action.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : !hasData ? (
            /* First-time User State */
            <div className="space-y-5 lg:space-y-6">
              {/* Welcome Message */}
              <div className="bg-gradient-to-br from-[#2563EB]/5 via-white to-[#06B6D4]/5 rounded-2xl lg:rounded-3xl p-5 lg:p-8 border border-slate-100">
                <div className="max-w-2xl">
                  <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-slate-900 mb-2 lg:mb-3">
                    Welcome to Decoded
                  </h1>
                  <p className="text-slate-600 text-sm lg:text-base mb-4 lg:mb-6">
                    Your AI-powered tariff classification and duty optimization platform. Get started by analyzing your first product.
                  </p>
                  <Button 
                    onClick={() => navigate(isMobile ? '/mobile' : '/analyzer')}
                    className="bg-gradient-to-r from-[#2563EB] to-[#06B6D4] text-white px-4 lg:px-6 py-2.5 lg:py-3 text-sm lg:text-base"
                  >
                    <Search className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                    Analyze Your First Product
                    <ArrowRight className="w-4 h-4 lg:w-5 lg:h-5 ml-2" />
                  </Button>
                </div>
              </div>

              {/* Placeholder Cards showing what insights will look like */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 border border-slate-100 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#059669]/5 to-transparent" />
                  <div className="relative flex sm:block items-center gap-4">
                    <div className="flex items-center gap-3 mb-0 sm:mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-5 h-5 text-[#059669]" />
                      </div>
                      <span className="text-sm font-medium text-slate-600 sm:hidden">Savings Potential</span>
                    </div>
                    <span className="text-sm font-medium text-slate-600 hidden sm:block">Savings Potential</span>
                    <div className="ml-auto sm:ml-0">
                      <div className="text-2xl lg:text-3xl font-bold text-slate-300 mb-0 sm:mb-2">$--,---</div>
                      <p className="text-[10px] lg:text-xs text-slate-400 hidden sm:block">Analyze products to discover savings</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 border border-slate-100 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#F59E0B]/5 to-transparent" />
                  <div className="relative flex sm:block items-center gap-4">
                    <div className="flex items-center gap-3 mb-0 sm:mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
                      </div>
                      <span className="text-sm font-medium text-slate-600 sm:hidden">Risk Alerts</span>
                    </div>
                    <span className="text-sm font-medium text-slate-600 hidden sm:block">Risk Alerts</span>
                    <div className="ml-auto sm:ml-0">
                      <div className="text-2xl lg:text-3xl font-bold text-slate-300 mb-0 sm:mb-2">--</div>
                      <p className="text-[10px] lg:text-xs text-slate-400 hidden sm:block">Items requiring review</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 border border-slate-100 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2563EB]/5 to-transparent" />
                  <div className="relative flex sm:block items-center gap-4">
                    <div className="flex items-center gap-3 mb-0 sm:mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <span className="text-sm font-medium text-slate-600 sm:hidden">SKUs Analyzed</span>
                    </div>
                    <span className="text-sm font-medium text-slate-600 hidden sm:block">SKUs Analyzed</span>
                    <div className="ml-auto sm:ml-0">
                      <div className="text-2xl lg:text-3xl font-bold text-slate-300 mb-0 sm:mb-2">--</div>
                      <p className="text-[10px] lg:text-xs text-slate-400 hidden sm:block">Products classified</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Dashboard with Data */
            <div className="space-y-4 lg:space-y-5">
              
              {/* 1. Financial Impact Overview - Primary Visual */}
              <div className="grid lg:grid-cols-12 gap-3 lg:gap-4">
                {/* Main Savings Card */}
                <div className="lg:col-span-5 bg-gradient-to-br from-[#059669] to-[#10B981] rounded-2xl lg:rounded-3xl p-5 lg:p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 lg:w-64 h-40 lg:h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-20 lg:w-32 h-20 lg:h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                  
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3 lg:mb-4">
                      <div className="w-9 h-9 lg:w-10 lg:h-10 bg-white/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5" />
                      </div>
                      <span className="text-white/80 font-medium text-sm lg:text-base">Savings Identified</span>
                    </div>
                    
                    <div className="text-3xl lg:text-5xl font-bold mb-1 lg:mb-2">
                      {formatCurrency(dashboardData.totalSavings || 0)}
                    </div>
                    <p className="text-white/70 text-xs lg:text-sm">
                      Potential duty optimization across {dashboardData.totalSkus} SKUs
                    </p>
                    
                    <Button 
                      onClick={() => navigate('/analyzer?tab=financial')}
                      variant="secondary"
                      className="mt-4 lg:mt-6 bg-white/20 hover:bg-white/30 text-white border-0 text-sm"
                    >
                      View Financial Impact
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>

                {/* Duty Exposure Distribution */}
                <div className="lg:col-span-7 bg-white rounded-2xl lg:rounded-3xl p-4 lg:p-6 border border-slate-100">
                  <div className="flex items-center justify-between mb-4 lg:mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl bg-gradient-to-br from-[#2563EB]/10 to-[#06B6D4]/10 flex items-center justify-center">
                        <PieChart className="w-4 h-4 lg:w-5 lg:h-5 text-[#2563EB]" />
                      </div>
                      <span className="font-semibold text-slate-900 text-sm lg:text-base">Platform Overview</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 lg:gap-4">
                    <div className="text-center p-2.5 lg:p-4 bg-gradient-to-br from-[#2563EB]/5 to-[#2563EB]/10 rounded-xl lg:rounded-2xl">
                      <div className="text-xl lg:text-3xl font-bold text-[#2563EB] mb-0.5 lg:mb-1">{dashboardData.totalAnalyses}</div>
                      <p className="text-[10px] lg:text-xs text-slate-600">Total Analyses</p>
                    </div>
                    <div className="text-center p-2.5 lg:p-4 bg-gradient-to-br from-[#06B6D4]/5 to-[#06B6D4]/10 rounded-xl lg:rounded-2xl">
                      <div className="text-xl lg:text-3xl font-bold text-[#06B6D4] mb-0.5 lg:mb-1">{dashboardData.totalSkus}</div>
                      <p className="text-[10px] lg:text-xs text-slate-600">SKUs Classified</p>
                    </div>
                    <div className="text-center p-2.5 lg:p-4 bg-gradient-to-br from-[#10B981]/5 to-[#10B981]/10 rounded-xl lg:rounded-2xl">
                      <div className="text-xl lg:text-3xl font-bold text-[#10B981] mb-0.5 lg:mb-1">{dashboardData.highConfidenceRate}%</div>
                      <p className="text-[10px] lg:text-xs text-slate-600">High Confidence</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Risk & Compliance */}
              {(dashboardData.flaggedItems > 0) && (
                <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 border border-slate-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl bg-[#F59E0B]/10 flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 lg:w-5 lg:h-5 text-[#F59E0B]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 text-sm lg:text-base">Risk & Compliance</h3>
                        <p className="text-[10px] lg:text-xs text-slate-500">Items requiring attention</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-xs lg:text-sm"
                      onClick={() => navigate('/governance')}
                    >
                      View Details
                      <ArrowRight className="w-3.5 h-3.5 lg:w-4 lg:h-4 ml-2" />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-2.5 lg:p-3 bg-red-50 rounded-lg lg:rounded-xl">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <div className="w-7 h-7 lg:w-8 lg:h-8 bg-red-100 rounded-md lg:rounded-lg flex items-center justify-center">
                        <AlertCircle className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-red-600" />
                      </div>
                      <span className="text-xs lg:text-sm font-medium text-slate-700">High Risk Items</span>
                    </div>
                    <span className="text-base lg:text-lg font-bold text-red-600">{dashboardData.flaggedItems}</span>
                  </div>
                </div>
              )}

              {/* AI Insights Widget */}
              <div className="bg-gradient-to-br from-[#6366F1]/5 to-[#06B6D4]/5 rounded-xl lg:rounded-2xl p-4 lg:p-6 border border-[#6366F1]/20">
                <div className="flex items-center justify-between mb-4 lg:mb-5">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
                      <Brain className="w-4 h-4 lg:w-5 lg:h-5 text-[#6366F1]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm lg:text-base">AI Insights</h3>
                      <p className="text-[10px] lg:text-xs text-slate-500">Savings & accuracy metrics</p>
                    </div>
                  </div>
                  {aiInsights?.accuracy_rate > 0 && (
                    <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 rounded-full">
                      <ThumbsUp className="w-3 h-3 text-emerald-600" />
                      <span className="text-xs font-semibold text-emerald-700">{aiInsights.accuracy_rate}% accurate</span>
                    </div>
                  )}
                </div>

                {aiInsights ? (
                  <div className="space-y-4">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="w-3.5 h-3.5 text-[#6366F1]" />
                          <span className="text-xs text-slate-500">Confidence</span>
                        </div>
                        <p className="text-lg font-bold text-slate-900">{aiInsights.high_confidence_rate || 0}%</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart3 className="w-3.5 h-3.5 text-[#06B6D4]" />
                          <span className="text-xs text-slate-500">This Month</span>
                        </div>
                        <p className="text-lg font-bold text-slate-900">{aiInsights.ai_performance?.classifications_this_month || 0}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart3 className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-xs text-slate-500">Total</span>
                        </div>
                        <p className="text-lg font-bold text-slate-900">{aiInsights.total_classifications || 0}</p>
                      </div>
                    </div>

                    {/* Top Savings */}
                    {aiInsights.top_savings_opportunities?.length > 0 && (
                      <div className="bg-white rounded-xl p-3 border border-slate-100">
                        <p className="text-xs font-medium text-slate-500 mb-2">Top Savings Opportunities</p>
                        <div className="space-y-2">
                          {aiInsights.top_savings_opportunities.slice(0, 3).map((opp, i) => (
                            <div 
                              key={i} 
                              className="flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50 rounded-lg p-1.5 -mx-1.5 transition-colors"
                              onClick={() => navigate(`/analysis/${opp.analysis_id}`)}
                            >
                              <span className="text-slate-700 truncate flex-1 mr-2">{opp.description || 'Product'}</span>
                              <span className="text-emerald-600 font-semibold whitespace-nowrap">
                                ${opp.savings?.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 lg:py-8 text-slate-400">
                    <Brain className="w-7 h-7 lg:w-8 lg:h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs lg:text-sm">Run classifications to see AI insights</p>
                  </div>
                )}
              </div>

              {/* 3. Recent Activity */}
              <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 border border-slate-100">
                <div className="flex items-center justify-between mb-4 lg:mb-5">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl bg-slate-100 flex items-center justify-center">
                      <Activity className="w-4 h-4 lg:w-5 lg:h-5 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm lg:text-base">Recent Activity</h3>
                      <p className="text-[10px] lg:text-xs text-slate-500">Latest analyses and updates</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/history')} className="text-xs lg:text-sm">
                    View All
                    <ArrowRight className="w-3.5 h-3.5 lg:w-4 lg:h-4 ml-1" />
                  </Button>
                </div>

                {recentActivity.length > 0 ? (
                  <div className="space-y-2 lg:space-y-3">
                    {recentActivity.map((item, i) => (
                      <div 
                        key={i}
                        className="flex items-center gap-3 lg:gap-4 p-2.5 lg:p-3 hover:bg-slate-50 rounded-lg lg:rounded-xl cursor-pointer transition-colors"
                        onClick={() => navigate(`/analysis/${item.id}`)}
                      >
                        <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl flex items-center justify-center flex-shrink-0 ${
                          item.type === 'bulk' ? 'bg-[#06B6D4]/10' : 
                          item.type === 'financial_impact' ? 'bg-[#059669]/10' : 'bg-[#2563EB]/10'
                        }`}>
                          {item.type === 'bulk' ? (
                            <FileText className="w-4 h-4 lg:w-5 lg:h-5 text-[#06B6D4]" />
                          ) : item.type === 'financial_impact' ? (
                            <DollarSign className="w-4 h-4 lg:w-5 lg:h-5 text-[#059669]" />
                          ) : (
                            <Search className="w-4 h-4 lg:w-5 lg:h-5 text-[#2563EB]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate text-sm lg:text-base">{item.title}</p>
                          <p className="text-[10px] lg:text-xs text-slate-500">{item.skus} SKU{item.skus > 1 ? 's' : ''}</p>
                        </div>
                        <div className="flex items-center gap-1.5 lg:gap-2 text-[10px] lg:text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          <span className="hidden sm:inline">{formatDate(item.date)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 lg:py-8 text-slate-400">
                    <Activity className="w-7 h-7 lg:w-8 lg:h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs lg:text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardHome;
